import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { resetUserPassword } from '@/entities/user';
import { FeatureFlag, useFeatureFlag } from '@/shared/feature-flags';
import { authManager } from '@/shared/utils/auth/auth-manager';
import { type LoginFormValues, loginSchema } from '../model/auth.schema';
import { AuthorizationFormPanel, type ViewState } from './authorization/AuthorizationFormPanel';
import { AuthorizationShell } from './authorization/AuthorizationShell';

const PASSWORD_RESET_COOLDOWN_SECONDS = 30;
const PASSWORD_RESET_STORAGE_KEY = 'passwordResetTimestamps';

interface LoginPageProps {
    onLoginSuccess: () => void;
}

export function LoginPage({ onLoginSuccess }: LoginPageProps) {
    const [view, setView] = useState<ViewState>('LOGIN');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [passwordResetEmail, setPasswordResetEmail] = useState('');
    const [isLoadingPasswordReset, setIsLoadingPasswordReset] = useState(false);
    const [passwordResetSent, setPasswordResetSent] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState(0);

    const showEmailPassword = useFeatureFlag(FeatureFlag.F_USE_EMAIL_PASS_AUTH);

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
            remember: false,
        },
    });

    async function handleOneLogin() {
        setIsLoading(true);
        setError(null);

        try {
            await authManager.logIn();
            onLoginSuccess();
        } catch (err) {
            console.error('Login failed:', err);
            setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }

    async function handleEmailLogin(data: LoginFormValues) {
        setIsLoading(true);
        setError(null);

        try {
            await authManager.logInWithEmailAndPassword(data.email, data.password);
            onLoginSuccess();
        } catch (err: unknown) {
            const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : undefined;

            if (code === 'auth/user-not-found' || code === 'auth/wrong-password') {
                setError('Invalid email or password');
            } else {
                setError(err instanceof Error ? err.message : 'An error occurred during sign in');
            }
        } finally {
            setIsLoading(false);
        }
    }

    function handleSwitchToPasswordReset() {
        setPasswordResetEmail(getValues('email') || passwordResetEmail);
        setPasswordResetSent(false);
        setView('PASSWORD_RESET');
    }

    async function handleSendPasswordReset() {
        if (!passwordResetEmail || !/.+@.+\..+/.test(passwordResetEmail) || cooldownRemaining > 0) return;

        setIsLoadingPasswordReset(true);
        setError(null);

        try {
            await resetUserPassword({ email: passwordResetEmail, continueUrl: window.location.origin });
            setPasswordResetSent(true);
            localStorage.setItem(PASSWORD_RESET_STORAGE_KEY, String(Date.now()));
        } catch {
            setPasswordResetSent(true);
        } finally {
            setIsLoadingPasswordReset(false);
        }
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('fromInvite') === '1' && showEmailPassword) {
            setView('EMAIL_LOGIN');
            const emailParam = params.get('email');
            if (emailParam) {
                setValue('email', emailParam);
            }
            const url = new URL(window.location.href);
            url.searchParams.delete('fromInvite');
            url.searchParams.delete('email');
            window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
        }
    }, [setValue, showEmailPassword]);

    useEffect(() => {
        if (view !== 'PASSWORD_RESET') return;

        const tick = () => {
            const lastReset = Number(localStorage.getItem(PASSWORD_RESET_STORAGE_KEY) ?? 0);
            const elapsed = Math.floor((Date.now() - lastReset) / 1000);
            const remaining = Math.max(0, PASSWORD_RESET_COOLDOWN_SECONDS - elapsed);
            setCooldownRemaining(remaining);
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [view]);

    const isResetDisabled = !/.+@.+\..+/.test(passwordResetEmail) || cooldownRemaining > 0 || isLoadingPasswordReset;

    return (
        <AuthorizationShell>
            <AuthorizationFormPanel
                view={view}
                error={error}
                isLoading={isLoading}
                showEmailPassword={showEmailPassword}
                passwordResetEmail={passwordResetEmail}
                isLoadingPasswordReset={isLoadingPasswordReset}
                passwordResetSent={passwordResetSent}
                cooldownRemaining={cooldownRemaining}
                errors={errors}
                register={register}
                handleSubmit={handleSubmit}
                onDismissError={() => setError(null)}
                onOneLogin={handleOneLogin}
                onShowEmailLogin={() => setView('EMAIL_LOGIN')}
                onBackToLogin={() => setView('LOGIN')}
                onEmailLogin={handleEmailLogin}
                onShowPasswordReset={handleSwitchToPasswordReset}
                onPasswordResetEmailChange={setPasswordResetEmail}
                onCancelPasswordReset={() => setView('EMAIL_LOGIN')}
                onSendPasswordReset={handleSendPasswordReset}
                isResetDisabled={isResetDisabled}
            />
        </AuthorizationShell>
    );
}
